import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Upload, ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Reusable image uploader for admin forms.
 *
 * Props:
 *  - value: current image URL (string)
 *  - onChange: (url: string) => void
 *  - category: backend upload category ("player-photo" | "team-logo" | "competition-logo" | "article-image" | "match-photo")
 *  - label: field label (default "Image")
 *  - previewShape: "square" (players/logos) or "wide" (article banners)
 *  - testId: base data-testid used to derive uploader/input/preview ids
 */
const ImageUploader = ({
  value,
  onChange,
  category,
  label = "Image",
  previewShape = "square",
  testId = "image",
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, WEBP)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image is over 5 MB — please compress it first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/uploads/${category}`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const isWide = previewShape === "wide";
  const previewClass = isWide
    ? "w-32 h-20 object-cover"
    : "w-20 h-20 object-cover";
  const placeholderClass = isWide
    ? "w-32 h-20 border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center"
    : "w-20 h-20 border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center";

  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="flex items-start gap-3 mt-1" data-testid={`${testId}-uploader`}>
        {value ? (
          <img
            src={value}
            alt="Preview"
            className={`${previewClass} border border-gray-300 flex-shrink-0`}
            onError={(e) => { e.target.style.display = "none"; }}
            data-testid={`${testId}-preview`}
          />
        ) : (
          <div className={`${placeholderClass} flex-shrink-0`}>
            <ImageIcon className="w-8 h-8 text-gray-300" />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <label className="cursor-pointer inline-flex items-center gap-2 bg-[#09090b] text-white px-3 py-2 text-xs uppercase font-bold tracking-widest hover:bg-[#FFC72C] hover:text-black transition">
              <Upload className="w-3.5 h-3.5" />
              {uploading ? "Uploading…" : "Upload"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                disabled={uploading}
                data-testid={`${testId}-upload-input`}
              />
            </label>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="text-xs text-red-600 hover:underline"
                data-testid={`${testId}-clear-btn`}
              >
                Clear
              </button>
            )}
          </div>
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="rounded-none text-xs"
            placeholder="…or paste an image URL"
            data-testid={`${testId}-url-input`}
          />
          <p className="text-[10px] text-gray-500">JPG, PNG, WEBP up to 5 MB.</p>
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
